/**
 * Search area — ICCID lookup and real-time BLE discovery.
 *
 * Desktop: renders RadioGrid (the full server-backed radio table) so users
 * can filter/sort all radios they have access to. Manual ICCID search is
 * available via the grid's ICCID filter.
 *
 * Mobile / native: keeps the card-based flow — manual ICCID entry +
 * QR/barcode scan resolve via resolveRadios, and the BLE Nearby section
 * shows auto-detected radios sorted nearest-first. Both paths render
 * NearbyUnitCard with the flat radio row shape.
 *
 * Available to all authenticated users — the resolve and list endpoints
 * scope non-staff to their own sites.
 *
 * Contains:
 * - SearchView — the route component (default export)
 */

import { Capacitor } from '@capacitor/core';
import { BarcodeFormat, BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveRadios } from '@sophocles/api/radios';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import { useBluetooth } from '@sophocles/components/bluetooth/BluetoothContext';
import useNearbyUnitResolution from '@sophocles/features/bluetooth/hooks/useNearbyUnitResolution';
import usePersistedState from '@sophocles/hooks/usePersistedState';

import NearbyUnitCard from './components/NearbyUnitCard';
import RadioGrid from './components/RadioGrid';

/** Shortest manual ICCID we'll resolve — mirrors MIN_RESOLVE_ICCID_LEN on the
 * backend so a too-short suffix can't match the wrong unit. */
const MIN_ICCID_LENGTH = 7;

/** Stat-card "raised pad" styling for the QR / BLE scan action cards. */
const scanCardSx = {
  flex: 1,
  borderRadius: 'var(--card-radius)',
  borderColor: 'var(--card-border)',
  bgcolor: 'var(--card-bg)',
  transition:
    'box-shadow var(--duration-fast) var(--easing-standard), border-color var(--duration-normal) var(--easing-standard)',
  boxShadow: 'var(--card-shadow-raised)',
  '&:hover': { boxShadow: 'var(--card-shadow-raised-hover)' },
  '&:active': { boxShadow: 'var(--card-shadow-raised-active)' },
};

function SearchView({ loading, ...props }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNative = Capacitor.isNativePlatform();

  const { isScanning, discoveredUnits, startScan, stopScan } = useBluetooth();

  // --- BLE nearby resolution (native only) ---
  const { resolvedMap, isResolving } = useNearbyUnitResolution(discoveredUnits);

  useEffect(() => {
    if (!isNative) return;
    startScan();
    return () => {
      stopScan();
    };
    // startScan and stopScan are stable useCallback refs from context —
    // listing them satisfies exhaustive-deps without causing re-scan loops.
  }, [isNative, startScan, stopScan]);

  // Join discovered units (live RSSI) with resolved map, keep only resolved,
  // sort nearest-first by RSSI descending (less negative = stronger signal).
  const resolvedUnits = Array.from(resolvedMap.values())
    .filter(resolution => discoveredUnits.has(resolution.iccid))
    .map(resolution => ({
      resolution,
      rssi: discoveredUnits.get(resolution.iccid)?.rssi ?? null,
    }))
    .sort((a, b) => {
      if (a.rssi == null && b.rssi == null) return 0;
      if (a.rssi == null) return 1;
      if (b.rssi == null) return -1;
      return b.rssi - a.rssi;
    });

  const isSearching = isNative && isScanning && resolvedUnits.length === 0;
  const isIdle = isNative && !isScanning && resolvedUnits.length === 0;

  // --- Manual / scan search (mobile + native only) ---
  // Persisted to sessionStorage so drilling into a result and coming back
  // (via the sidebar Search field or the "Results" shortcut) restores the
  // last lookup instead of dropping the user on a blank page.
  const [iccidInput, setIccidInput] = usePersistedState('search:iccidInput', '');
  const [searchResults, setSearchResults] = usePersistedState('search:results', []);
  const [searchPerformed, setSearchPerformed] = usePersistedState('search:performed', false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Keep a mounted flag so async callbacks don't update unmounted state.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const runResolve = async iccid => {
    const trimmed = iccid.trim();
    if (!trimmed) return;
    if (trimmed.length < MIN_ICCID_LENGTH) {
      setSearchError(
        t('search.iccidTooShort', 'Enter at least {{count}} characters to search.', { count: MIN_ICCID_LENGTH }),
      );
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchPerformed(false);

    try {
      const results = await resolveRadios([trimmed]);
      if (!mountedRef.current) return;
      setSearchResults(results);
      setSearchPerformed(true);
    } catch {
      if (!mountedRef.current) return;
      setSearchError(t('search.resolveError', 'Something went wrong. Please try again.'));
      setSearchPerformed(true);
    } finally {
      if (mountedRef.current) setSearchLoading(false);
    }
  };

  const handleSearchSubmit = () => {
    runResolve(iccidInput);
  };

  const handleInputKeyDown = e => {
    if (e.key === 'Enter') {
      runResolve(iccidInput);
    }
  };

  const handleScan = async () => {
    try {
      const { barcodes } = await BarcodeScanner.scan({ formats: [BarcodeFormat.QrCode] });
      const iccid = barcodes[0]?.rawValue;
      if (!iccid) return;
      setIccidInput(iccid);
      runResolve(iccid);
    } catch {
      // User cancelled or denied permission — no action needed.
    }
  };

  // Desktop: render the full RadioGrid — ICCID filter replaces manual entry.
  if (!isMobile) {
    return (
      <AppNav flex notFullHeight loading={loading} page="search" {...props}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
            padding: '0 var(--page-px) var(--page-py)',
          }}
        >
          <TitleBar title={t('search.title', 'Search')} />
          <RadioGrid persistScope="searchRadioGrid" />
        </Box>
      </AppNav>
    );
  }

  // Mobile / native: card-based flow with manual entry + QR scan + BLE nearby.
  return (
    <AppNav flex notFullHeight loading={loading} page="search" {...props}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          padding: '0 var(--page-px) var(--page-py)',
        }}
      >
        <TitleBar title={t('search.title', 'Search')} />

        {/* Native scan actions — stat-style cards, OUTSIDE the input panel. */}
        {isNative && (
          <Stack direction="row" spacing={'var(--space-2)'} sx={{ pt: 'var(--page-gap)' }}>
            <Card variant="outlined" sx={scanCardSx}>
              <CardActionArea
                onClick={handleScan}
                disabled={searchLoading}
                sx={{ py: 'var(--space-3)', px: 'var(--space-2)', textAlign: 'center' }}
                aria-label={t('search.scanQr', 'Scan QR')}
              >
                <QrCodeScannerIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                  {t('search.scanQr', 'Scan QR')}
                </Typography>
              </CardActionArea>
            </Card>
            <Card variant="outlined" sx={scanCardSx}>
              <CardActionArea
                onClick={isScanning ? stopScan : startScan}
                sx={{ py: 'var(--space-3)', px: 'var(--space-2)', textAlign: 'center' }}
                aria-label={isScanning ? t('search.stopBleScan', 'Stop scan') : t('search.startBleScan', 'Scan nearby')}
              >
                <BluetoothSearchingIcon sx={{ fontSize: 36, color: isScanning ? 'primary.main' : 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                  {isScanning ? t('search.stopBleScan', 'Stop scan') : t('search.startBleScan', 'Scan nearby')}
                </Typography>
              </CardActionArea>
            </Card>
          </Stack>
        )}

        {/* Manual ICCID entry — inset panel mirroring the grid filter panel. */}
        <Box
          sx={{
            mt: 'var(--page-gap)',
            mb: 'var(--space-3)',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <TextField
            fullWidth
            size="small"
            variant="filled"
            hiddenLabel
            value={iccidInput}
            onChange={e => setIccidInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={t('search.iccidPlaceholder', 'Enter ICCID')}
            aria-label={t('search.iccidPlaceholder', 'Enter ICCID')}
            disabled={searchLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t('search.searchButton', 'Search')}
                    onClick={handleSearchSubmit}
                    disabled={searchLoading || !iccidInput.trim()}
                  >
                    {searchLoading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Search results */}
        {searchError && (
          <Alert severity="error" sx={{ mb: 'var(--space-3)' }}>
            {searchError}
          </Alert>
        )}

        {searchPerformed && !searchError && (
          <Box sx={{ mb: 'var(--space-4)' }}>
            <Typography
              variant="overline"
              sx={{ color: 'var(--text-secondary)', display: 'block', mb: 'var(--space-2)' }}
            >
              {t('search.results', 'Results')}
            </Typography>

            {searchResults.length > 0 ? (
              searchResults.map(resolution => <NearbyUnitCard key={resolution.iccid} resolution={resolution} />)
            ) : (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                {t('search.noMatch', 'No matching unit found, or it isn’t in your account.')}
              </Typography>
            )}
          </Box>
        )}

        {/* BLE Nearby section — native only */}
        {isNative && (
          <>
            {searchPerformed && <Divider sx={{ mb: 'var(--space-4)' }} />}

            <Typography
              variant="overline"
              sx={{ color: 'var(--text-secondary)', display: 'block', mb: 'var(--space-2)' }}
            >
              {t('search.nearby', 'Nearby')}
            </Typography>

            {/* Scanning indicator — no resolved results yet */}
            {isSearching && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: 'var(--space-3)',
                }}
              >
                <BluetoothSearchingIcon sx={{ fontSize: 64, color: 'var(--text-muted)' }} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
                    {t('search.searching', 'Searching for nearby units…')}
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* Not scanning, nothing found */}
            {isIdle && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: 'var(--space-3)',
                }}
              >
                <BluetoothSearchingIcon sx={{ fontSize: 64, color: 'var(--text-muted)' }} />
                <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
                  {t('search.noUnits', 'No nearby units found.')}
                </Typography>
              </Box>
            )}

            {/* Resolved unit list */}
            {resolvedUnits.length > 0 && (
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {(isScanning || isResolving) && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 'var(--space-3)' }}>
                    <CircularProgress size={14} />
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                      {t('search.scanning', 'Scanning…')}
                    </Typography>
                  </Stack>
                )}
                {resolvedUnits.map(({ resolution, rssi }) => (
                  <NearbyUnitCard key={resolution.iccid} resolution={resolution} rssi={rssi} />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </AppNav>
  );
}

export default SearchView;
